import React, { useEffect, useMemo, useRef } from "react";
import { Navigate, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "../misc/Button";
import { Card } from "../misc/Card";
import CardGrid from "./CardGrid";
import AddPostDialog from "./AddPostDialog";
import ListingPickDialog from "./ListingPickDialog";
import { useAddPostPageSwitch } from "./useAddPostPageEnabled";
import { scrollPageTop } from "./scrollTop";
import { useListingList } from "./useListingList";
import { useHasListings } from "./useHasListings";
import { OPENED_FROM_GRID } from "./useAddPostNavigation";
import { useZapcapUsage } from "../../hooks/useZapcapUsage";
import { useNiche } from "../niche/useNiche";
import { addPostPath, parseCardSlug } from "./rules/deepLinks";
import { isPostableRow, isPresetCard, isPropertyRow } from "./preset";

/** The Add Post page: the card grid, and over it the dialog of the card named in the route. */
const AddPostScreen: React.FC = () => {
    const { enabled, resolved } = useAddPostPageSwitch();
    const { card: rawCard } = useParams();
    const [search] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();
    const listingList = useListingList();
    // The campaign list has no row for a listing without a campaign, so with no property campaign the
    // page asks once whether the account has a listing at all; the cards' note depends on it.
    const noPropertyCampaign = !listingList.loading && !listingList.error && !listingList.rows.some(isPropertyRow);
    const hasListings = useHasListings(noPropertyCampaign);
    const { usage } = useZapcapUsage();
    const niche = useNiche(true);
    const card = parseCardSlug(rawCard);
    const deepLinkId = search.get("listing");
    const preset = useMemo(
        () => (card && isPresetCard(card) ? { card, campaignId: deepLinkId ?? undefined } : null),
        [card, deepLinkId],
    );
    const headingRef = useRef<HTMLHeadingElement>(null);
    // The grid is a screen: it opens at the top with its heading focused. A card is a dialog over it, so
    // opening or closing one leaves the page where it was.
    useEffect(() => {
        if (card || !resolved) return;
        scrollPageTop();
        headingRef.current?.focus();
    }, [card, resolved]);

    // A cold load waits for the switch (later visits have it cached): with it off the route is never a
    // dead end, it opens the old dialog over the calendar, and the page never flashes first.
    if (!resolved) return null;
    if (!enabled) return <Navigate to="/" state={{ addPost: true }} replace />;

    const propertyRows = listingList.rows.filter(isPropertyRow);
    const rowsKnown = !listingList.loading && !listingList.error;
    const ctx = {
        listingCount: rowsKnown ? propertyRows.length : null,
        activeListingCount: rowsKnown ? propertyRows.filter(isPostableRow).length : null,
        hasListings,
        // Up, not down: the server refuses only at zero, so 1–59 s left is still a minute to use.
        captionMinutesLeft: usage ? Math.max(0, Math.ceil((usage.limitSeconds - usage.usedSeconds) / 60)) : null,
        nicheEligible: niche.overview ? !!niche.overview.eligible : null,
    };
    const dialog = !card ? null : preset ? (
        <AddPostDialog key={card} preset={preset} niche={niche} />
    ) : (
        <ListingPickDialog
            key={card}
            card={card as "open-house" | "video-showcase"}
            rows={listingList.rows}
            rowsLoading={listingList.loading}
            rowsError={listingList.error}
            hasListings={hasListings}
            campaignId={deepLinkId}
        />
    );
    return (
        <div className="mx-auto w-full max-w-5xl px-4 py-6">
            <Card className="mt-0 p-4 min-[640px]:p-6">
                <Button
                    id="add-post-back-btn"
                    // "default" = the first entry in the app's history (a cold load or a bookmark): nothing to go back to.
                    onClick={() => (location.key === "default" ? navigate("/") : navigate(-1))}
                    className="mb-4 flex items-center"
                >
                    <ArrowLeft className="mr-2 h-5 w-5" /> Back
                </Button>
                <h1 ref={headingRef} tabIndex={-1} className="mb-1 text-2xl font-bold text-primary focus:outline-none">Add Post</h1>
                <p className="mb-6 text-gray-600">What do you want to post?</p>
                {listingList.loading ? <p className="mb-3 text-sm text-gray-500">Loading your campaigns…</p> : null}
                <CardGrid ctx={ctx} onPick={(slug) => navigate(addPostPath(slug), { state: OPENED_FROM_GRID })} />
            </Card>
            {dialog}
        </div>
    );
};
export default AddPostScreen;
